import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import firebase from 'firebase/app';
import qs from 'qs';
import '../../../App.scss';
import 'antd/dist/antd.css';
import {
  Avatar, Card, Button, Empty, Input, Typography, Space, Form, Select, Tooltip, Row, Col,
} from 'antd';
import { Redirect, useParams, useLocation, useHistory } from 'react-router-dom';
import {
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { ClaimOptionsChooser } from '../../../components/client/claim/ClaimOptionsChooser';
import { CreateTeamFormMinimal } from '../../../components/client/CreateTeamFormMinimal';
import ClientAvatar from '../../../components/client/ClientAvatar';
import CenteredSpinner from '../../../components/CenteredSpinner';
import TrackerService from '../../../utils/TrackerService';
import StrikeTeamsModel from '../../../models/StrikeTeamsModel';
import { ClaimTagSuccessCard, PersonalTagClaimSuccessCard } from '../../../components/client/ClaimTagSuccessCard';
import { supportedCurrencies } from '../../../utils/CurrencyUtils';
import { useCurrentUser } from '../../../models/useCurrentUser';
import { useUser } from '../../../models/useUser';
import { useClientForOwner } from '../../../models/useClientForOwner';
import { useTag } from '../../../models/useTag';
import { useTemplate } from '../../../models/useTemplate';
import { useBrandProduct } from '../../../contexts/BrandContext';
import { useClaimCopy } from '../../../models/useClaimCopy';
import { useClaimScreenCurrency } from '../../../models/useClaimScreenCurrency';
import { mergeClaimTagWithTemplate } from '../../../utils/mergeClaimTagWithTemplate';

const { Option } = Select;
const { Text, Title, Paragraph } = Typography;

const PRODUCTS_SUPPORTING_TEAMS = ['tipping'];

function useTeamsForClient(clientId, limit) {
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!clientId) {
      setTeams([]);
      return;
    }
    const model = new StrikeTeamsModel({ clientId, onUpdate: setTeams, limit: limit || undefined });
    model.fetchAllTeamsForClient();
    return () => model.stop();
  }, [clientId, limit]);

  return teams;
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function ConfirmClaimTagScreen({ locStrings }) {
  const { tagId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const queryParams = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search],
  );

  // ── Auth (only source of login truth) ──
  const [authLoading, authUser] = useCurrentUser();
  const uid = authUser?.uid;
  const isAnonymous = authUser?.isAnonymous;

  // ── Firestore user (always subscribed) ──
  const [, firestoreUser] = useUser(uid && !isAnonymous ? uid : null); // eslint-disable-line no-unused-vars

  // ── Client (always subscribed via ownerId) ──
  const [clientLoading, client] = useClientForOwner(uid && !isAnonymous ? uid : null);

  // ── Tag + template (always subscribed) ──
  const [tagLoading, rawTag] = useTag(tagId);
  const [templateLoading, template] = useTemplate(rawTag?.templateTagId);

  const tag = useMemo(() => {
    const merged = mergeClaimTagWithTemplate(rawTag, template);
    if (!merged) return null;
    if (queryParams.url) {
      return {
        ...merged,
        signupConfirmUrl: queryParams.url,
        partnerClientId: merged.partnerClientId || 'strike',
      };
    }
    return merged;
  }, [rawTag, template, queryParams.url]);

  // ── Brand (product-aware in claim flow) ──
  const { setProduct: setBrandProduct } = useBrandProduct();
  useEffect(() => {
    setBrandProduct(tag?.product || null);
    return () => setBrandProduct(null);
  }, [tag?.product, setBrandProduct]);

  // ── Teams (always subscribed, with expandable limit) ──
  const [teamLimit, setTeamLimit] = useState(100);
  const rawTeams = useTeamsForClient(client?.id, teamLimit);
  const teams = useMemo(() => {
    if (!rawTeams || rawTeams.length === 0) return rawTeams;
    return [...rawTeams].sort((a, b) =>
      (a.teamName || '').localeCompare(b.teamName || '', undefined, { sensitivity: 'base' })
    );
  }, [rawTeams]);
  const hasTeams = teams && teams.length > 0;

  // ── Local UI state ──
  const [step, setStep] = useState('choose');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [currency, applyUserCurrencyChoice, setCurrency] = useClaimScreenCurrency(client, tag);
  const [newTeamId, setNewTeamId] = useState(null);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimRoute, setClaimRoute] = useState(null);
  const [teamSearch, setTeamSearch] = useState('');

  // ── Derived ──
  const product = tag?.product;
  const isTeamsAllowed = PRODUCTS_SUPPORTING_TEAMS.includes(product);
  const isTeamOnly = !!tag?.isTeamTag;

  const optionCount = useMemo(() => {
    let n = 0;
    if (isTeamsAllowed && hasTeams) n += 1;
    if (isTeamsAllowed) n += 1;
    if (!isTeamOnly) n += 1;
    return n;
  }, [isTeamsAllowed, hasTeams, isTeamOnly]);

  const onlyOneOption = optionCount === 1;
  const onlyOptionIsCreateTeam = isTeamsAllowed && !hasTeams && isTeamOnly;

  const copy = useClaimCopy(tag, locStrings);

  // Claim completed: derive from tag subscription (clientId/status) rather than callback timing
  const claimCompleted = useMemo(() => {
    if (!tag || !client) return false;
    if (step === 'confirm-individual') {
      return tag.clientId === client.id;
    }
    if (step === 'confirm-team' && selectedTeam) {
      return tag.teamId === selectedTeam.teamId || tag.clientId === client.id;
    }
    return false;
  }, [tag, client, step, selectedTeam]);

  // Watch for newly created team appearing in subscription
  const newTeam = useMemo(() => {
    if (!newTeamId || !teams) return null;
    return teams.find((t) => t.teamId === newTeamId) || null;
  }, [newTeamId, teams]);

  useEffect(() => {
    if (newTeam && step === 'create-team') {
      setSelectedTeam(newTeam);
      setStep('confirm-team');
    }
  }, [newTeam, step]);

  // ── Claim funnel tracking ──
  const claimChooseTracked = useRef(false);
  useEffect(() => {
    if (step !== 'choose' || !tag || !client || claimChooseTracked.current) return;
    claimChooseTracked.current = true;
    const tracker = new TrackerService({ tagId, object: tag });
    const payload = TrackerService.buildClaimPayload(tag, client, uid, { step: 'choose' });
    tracker.mixpanelTrack('claim_choose_viewed', payload);
    if (!tracker.state.isTest) tracker.googleTrack('claim_choose_viewed', payload);
  }, [step, tag, client, uid, tagId]);

  // ── Navigation helpers ──
  const goBack = useCallback(() => {
    setSelectedTeam(null);
    setNewTeamId(null);
    setTeamSearch('');
    setStep('choose');
  }, []);

  const goToDashboard = useCallback(() => {
    if (client?.id) {
      history.push(`/client/manage/${client.id}`);
    }
  }, [client, history]);

  const trackClaimOption = useCallback((option) => {
    const tracker = new TrackerService({ tagId, object: tag });
    const payload = TrackerService.buildClaimPayload(tag, client, uid, { step: 'choose', option });
    tracker.mixpanelTrack('claim_option_selected', payload);
    if (!tracker.state.isTest) tracker.googleTrack('claim_option_selected', payload);
  }, [tag, client, uid, tagId]);

  // ── Team actions ──
  const handleSelectTeam = useCallback((team) => {
    setSelectedTeam(team);
    const next = team.defaultCurrency || currency;
    if (next) {
      applyUserCurrencyChoice(next);
    } else {
      setCurrency(null);
    }
    setStep('confirm-team');
  }, [currency, applyUserCurrencyChoice, setCurrency]);

  const handleCreateTeam = useCallback(async (formData) => {
    setCreatingTeam(true);
    try {
      const model = new StrikeTeamsModel({ clientId: client?.id });
      const teamId = await model.createTeamWithDefaultSettings({
        teamName: formData.teamName,
        teamCurrency: formData.defaultCurrency,
        templateTagId: tag?.templateTagId,
      });
      applyUserCurrencyChoice(formData.defaultCurrency);
      setNewTeamId(teamId);
    } catch (err) {
      console.error('Team creation failed:', err);
    } finally {
      setCreatingTeam(false);
    }

    const tracker = new TrackerService({ tagId, object: tag });
    const payload = TrackerService.buildClaimPayload(tag, client, uid, {
      teamOwnerClientId: client?.id,
      teamName: formData.teamName,
      teamCurrency: formData.defaultCurrency,
    });
    tracker.mixpanelTrack('create_team_while_claiming_tag', payload);
    tracker.mixpanelTrack('claim_create_team', payload);
    if (!tracker.state.isTest) {
      tracker.googleTrack('create_team_while_claiming_tag', payload);
      tracker.googleTrack('claim_create_team', payload);
    }
  }, [client, tag, uid, tagId, applyUserCurrencyChoice]);

  // ── Claim actions ──
  const handleClaimIndividual = useCallback(async () => {
    if (!currency || !tagId || !client) return;
    const tracker = new TrackerService({ tagId, object: tag });
    const basePayload = TrackerService.buildClaimPayload(tag, client, uid, { step: 'confirm-individual', currency });
    tracker.mixpanelTrack('claim_confirm_clicked', basePayload);
    if (!tracker.state.isTest) tracker.googleTrack('claim_confirm_clicked', basePayload);

    setClaiming(true);
    try {
      const claimFn = firebase.functions().httpsCallable('tagService-tagHandler-claimTag');
      const payload = {
        tagId,
        clientId: client.id,
        displayName: client.displayName || null,
        currency,
        tagUid: tag?.tagUid,
      };
      await claimFn(payload);

      const completedPayload = { distinct_id: uid, templateTagId: tag?.templateTagId || 'generic', ...payload };
      tracker.mixpanelTrack('confirm_claim_tag', completedPayload);
      tracker.mixpanelTrack('claim_completed', TrackerService.buildClaimPayload(tag, client, uid, { ...payload, step: 'completed' }));
      if (!tracker.state.isTest) {
        tracker.googleTrack('confirm_claim_tag', { userId: uid, ...completedPayload });
        tracker.googleTrack('claim_completed', TrackerService.buildClaimPayload(tag, client, uid, { ...payload, step: 'completed' }));
      }

      setClaimRoute(`/client/manage/${client.id}`);
      // Success is derived from tag subscription (clientId) – no setStep here
    } catch (err) {
      console.error('Claim failed:', err);
      setClaiming(false);
    }
    // claiming cleared when tag subscription shows claimCompleted (see effect below)
  }, [currency, tagId, client, tag, uid]);

  const handleClaimTeam = useCallback(async () => {
    if (!selectedTeam || !tagId || !client) return;
    const teamCurrency = selectedTeam.defaultCurrency || currency;
    const tracker = new TrackerService({ tagId, object: tag, teamId: selectedTeam.teamId });
    const basePayload = TrackerService.buildClaimPayload(tag, client, uid, {
      step: 'confirm-team',
      teamId: selectedTeam.teamId,
      teamName: selectedTeam.teamName,
      currency: teamCurrency,
    });
    tracker.mixpanelTrack('claim_confirm_clicked', basePayload);
    if (!tracker.state.isTest) tracker.googleTrack('claim_confirm_clicked', basePayload);

    setClaiming(true);
    try {
      const claimFn = firebase.functions().httpsCallable('tagService-tagHandler-claimTeamTag');
      const payload = {
        tagId,
        currency: teamCurrency,
        userClientId: client.id,
        displayName: client.displayName || null,
        teamId: selectedTeam.teamId,
        teamName: selectedTeam.teamName,
        tagUid: tag?.tagUid,
      };
      await claimFn(payload);

      const completedPayload = { distinct_id: uid, templateTagId: tag?.templateTagId || 'generic', ...payload };
      tracker.mixpanelTrack('confirm_claim_tag', completedPayload);
      tracker.mixpanelTrack('claim_completed', TrackerService.buildClaimPayload(tag, client, uid, { ...payload, step: 'completed' }));
      if (!tracker.state.isTest) {
        tracker.googleTrack('confirm_claim_tag', { userId: uid, ...completedPayload });
        tracker.googleTrack('claim_completed', TrackerService.buildClaimPayload(tag, client, uid, { ...payload, step: 'completed' }));
      }

      setClaimRoute(`/client/manage/${client.id}`);
      // Success is derived from tag subscription (teamId/clientId) – no setStep here
    } catch (err) {
      console.error('Team claim failed:', err);
      setClaiming(false);
    }
    // claiming cleared when tag subscription shows claimCompleted (see effect below)
  }, [selectedTeam, tagId, client, tag, uid, currency]);

  // Clear claiming when tag subscription confirms claim completed
  useEffect(() => {
    if (claiming && claimCompleted) {
      setClaiming(false);
    }
  }, [claiming, claimCompleted]);

  // ── Redirects ──
  if (!authLoading && (!authUser || isAnonymous)) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Redirect to={`/client/login?redirect=${redirect}`} />;
  }

  // Don't redirect when claiming, on confirm step, or when subscription shows claim completed
  const isConfirmStep = step === 'confirm-individual' || step === 'confirm-team';
  if (!clientLoading && !claiming && !claimCompleted && tag && tag.clientId && !isConfirmStep) {
    // If current client owns the tag, go to dashboard; otherwise go to tag (payer) URL
    if (client && client.id === tag.clientId) {
      return <Redirect to={`/client/manage/${tag.clientId}`} />;
    }
    return <Redirect to={`/tags/${tagId}`} />;
  }

  // ── Loading ──
  const isLoading = authLoading || clientLoading || tagLoading
    || (rawTag?.templateTagId && templateLoading);

  // Waiting for client to be created (e.g. just signed up; backend trigger creates client async)
  const waitingForClient = !clientLoading && !client && uid && !isAnonymous && tag;

  if (isLoading || !client || !tag) {
    if (waitingForClient) {
      return (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <CenteredSpinner loading />
          <Paragraph type="secondary" style={{ marginTop: 16 }}>
            {copy?.loading?.settingUpAccount ?? 'Setting up your account…'}
          </Paragraph>
          <Paragraph type="secondary" style={{ fontSize: 13, marginTop: 8 }}>
            {copy?.loading?.settingUpAccountHint ?? 'This usually takes a few seconds. If it takes longer, try refreshing the page.'}
          </Paragraph>
        </div>
      );
    }
    return <CenteredSpinner loading />;
  }

  if (client.status === 'paused') {
    return <Redirect to="/" />;
  }

  // ── Render ──
  const avatar = <ClientAvatar client={client} />;

  return (
    <div style={{ minHeight: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '24px 24px 0',
        maxWidth: 800,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}>
        {avatar}
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {client.displayName || client.email}
          </Title>
          {client.profileBio && <Text type="secondary">{client.profileBio}</Text>}
        </div>
      </div>

      <div style={{ padding: '16px 24px 48px', maxWidth: 800, margin: '0 auto' }}>

        {/* ── Step: Choose ── */}
        {step === 'choose' && (
          <ClaimOptionsChooser
            copy={copy}
            isTeamsAllowed={isTeamsAllowed}
            hasTeams={hasTeams}
            isTeamOnly={isTeamOnly}
            onlyOneOption={onlyOneOption}
            onlyOptionIsCreateTeam={onlyOptionIsCreateTeam}
            optionCount={optionCount}
            onSelectIndividual={() => { trackClaimOption('individual'); setStep('confirm-individual'); }}
            onSelectTeam={() => { trackClaimOption('team'); setStep('pick-team'); }}
            onSelectCreateTeam={() => { trackClaimOption('create_team'); setStep('create-team'); }}
            onGoToDashboard={goToDashboard}
          />
        )}

        {/* ── Step: Pick Team ── */}
        {step === 'pick-team' && (() => {
          const q = teamSearch.trim().toLowerCase();
          const filtered = q
            ? teams.filter((t) =>
              (t.teamName || '').toLowerCase().includes(q)
              || (t.teamDescription || '').toLowerCase().includes(q))
            : teams;
          const showSearch = teams.length > 5;
          const hitLimit = teams.length >= teamLimit;

          return (
            <>
              <BackButton onClick={goBack} copy={copy} />
              <Title level={3} style={{ marginTop: 8 }}>{copy.pickTeam.title}</Title>
              <Paragraph type="secondary">
                {copy.pickTeam.subtitle}
              </Paragraph>

              {showSearch && (
                <Input
                  prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                  placeholder={copy.pickTeam.searchPlaceholder}
                  allowClear
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  style={{ marginBottom: 16, maxWidth: 400, borderRadius: 8 }}
                  size="large"
                />
              )}

              <Row gutter={[12, 12]}>
                {filtered.map((team) => {
                  const memberCount = (team.teamNumberOfMembers || 0) - (team.teamNumberOfDeletedMembers || 0);
                  return (
                    <Col key={team.teamId} xs={24} sm={24} md={24} lg={12}>
                      <Card
                        hoverable
                        onClick={() => handleSelectTeam(team)}
                        style={{
                          borderRadius: 12,
                          border: '1px solid #e8e8e8',
                          cursor: 'pointer',
                          transition: 'box-shadow .2s, border-color .2s',
                        }}
                        bodyStyle={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px' }}
                      >
                        <Avatar
                          src={team.topLogo || null}
                          icon={<TeamOutlined />}
                          size={48}
                          shape="square"
                          style={{
                            borderRadius: 10,
                            flexShrink: 0,
                            background: team.rgbColor || '#f0f5ff',
                            color: team.topLogo ? undefined : '#1890ff',
                            fontSize: 22,
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: 15, display: 'block' }} ellipsis>
                            {team.teamName}
                          </Text>
                          {team.teamDescription && (
                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis>
                              {team.teamDescription}
                            </Text>
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {memberCount} {copy.pickTeam.memberCountLabel(memberCount)}
                          </Text>
                        </div>
                        <ArrowRightOutlined style={{ color: '#bbb', fontSize: 14, flexShrink: 0 }} />
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {filtered.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical" size={4} style={{ textAlign: 'center' }}>
                      <Text type="secondary">{copy.pickTeam.emptyStateTitle}</Text>
                      <Button type="link" onClick={() => { setTeamSearch(''); setStep('create-team'); }}>
                        <PlusCircleOutlined /> {copy.pickTeam.createNewTeamButton}
                      </Button>
                    </Space>
                  }
                  style={{ marginTop: 32 }}
                />
              )}

              {hitLimit && (
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <Button
                    type="default"
                    onClick={() => setTeamLimit((prev) => prev < 500 ? 500 : 1000)}
                  >
                    {copy.pickTeam.showMoreButton(teams.length)}
                  </Button>
                </div>
              )}
            </>
          );
        })()}

        {/* ── Step: Create Team ── */}
        {step === 'create-team' && (
          <>
            <BackButton onClick={goBack} disabled={creatingTeam || !!newTeamId} copy={copy} />
            <Title level={3} style={{ marginTop: 8 }}>
              {newTeamId ? copy.createTeamStep.settingUpTitle : copy.createTeamStep.title}
            </Title>

            {!newTeamId && (
              <>
                <Paragraph type="secondary">
                  {copy.createTeamStep.helperText}
                </Paragraph>
                <Card style={{ borderRadius: 12, maxWidth: 480 }}>
                  <CreateTeamFormMinimal
                    key="createTeamMini"
                    callback={handleCreateTeam}
                    edit={false}
                    loading={creatingTeam}
                    client={client}
                  />
                </Card>
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Button type="default" onClick={goToDashboard} style={{ width: '50%', minWidth: 'fit-content', borderRadius: 8, borderColor: '#d9d9d9', color: '#595959' }}>
                    {copy.buttons.continueWithoutSetup}
                  </Button>
                  <Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>{copy.choose.skipAssignText}</Text>
                </div>
              </>
            )}

            {newTeamId && !newTeam && (
              <div style={{ marginTop: 24 }}>
                <CenteredSpinner loading noLayout />
              </div>
            )}
          </>
        )}

        {/* ── Step: Confirm Team Claim ── */}
        {step === 'confirm-team' && selectedTeam && !claimCompleted && (
          <>
            <BackButton onClick={goBack} disabled={claiming} copy={copy} />
            <Title level={3} style={{ marginTop: 8 }}>
              {copy.confirmTeam.title}
            </Title>

            <Card style={{ borderRadius: 12, marginTop: 16 }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space align="center" size={14}>
                  <Avatar
                    src={selectedTeam.topLogo || null}
                    icon={<TeamOutlined />}
                    size={48}
                    shape="square"
                    style={{
                      borderRadius: 10,
                      background: selectedTeam.rgbColor || '#f0f5ff',
                      color: selectedTeam.topLogo ? undefined : '#1890ff',
                      fontSize: 22,
                    }}
                  />
                  <div>
                    <Text strong style={{ fontSize: 16, display: 'block' }}>{selectedTeam.teamName}</Text>
                    {selectedTeam.defaultCurrency && (
                      <Text type="secondary">
                        {selectedTeam.defaultCurrency}
                      </Text>
                    )}
                  </div>
                </Space>
                <Paragraph type="secondary">
                  {copy.confirmTeam.subtitle}
                </Paragraph>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  loading={claiming}
                  onClick={handleClaimTeam}
                  style={{ borderRadius: 8, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  {copy.buttons.confirmAndContinue}
                </Button>
              </Space>
            </Card>
          </>
        )}

        {/* ── Step: Confirm Individual Claim ── */}
        {step === 'confirm-individual' && !claimCompleted && (
          <>
            <BackButton onClick={goBack} disabled={claiming} copy={copy} />
            <Text type="secondary" style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginTop: 8 }}>
              Step 3 of 3
            </Text>
            <Title level={3} style={{ marginTop: 8 }}>
              {copy.confirmIndividual.title}
            </Title>
            <Paragraph type="secondary">
              {copy.confirmIndividual.subtitle}
            </Paragraph>

            <Card style={{ borderRadius: 12, marginTop: 16 }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Form layout="vertical">
                  <Form.Item
                    label={
                      <span>
                        {copy.confirmIndividual.selectCurrencyLabel}&nbsp;
                        <Tooltip title={copy.confirmIndividual.selectCurrencyTooltip}>
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                  >
                    <Select
                      value={currency}
                      allowClear={false}
                      onChange={applyUserCurrencyChoice}
                      placeholder={copy.confirmIndividual.selectCurrencyPlaceholder}
                      style={{ maxWidth: 300 }}
                    >
                      {Object.entries(supportedCurrencies).map(([code, label]) => (
                        <Option key={code} value={code}>{label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Form>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  loading={claiming}
                  disabled={!currency}
                  onClick={handleClaimIndividual}
                  style={{ borderRadius: 8, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  {copy.buttons.confirmAndContinue}
                </Button>
              </Space>
            </Card>
          </>
        )}

        {/* ── Step: Success (derived from tag subscription) ── */}
        {claimCompleted && tag && (
          tag.signupConfirmUrl && tag.partnerClientId ? (
            <ClaimTagSuccessCard
              templateTagId={tag.templateTagId}
              tagId={tagId}
              tag={tag}
              clientId={client.id}
              locStrings={locStrings}
            />
          ) : (
            <PersonalTagClaimSuccessCard
              teamId={selectedTeam?.teamId}
              teamName={selectedTeam?.teamName}
              isNewTeam={!!newTeamId && newTeamId === selectedTeam?.teamId}
              templateTagId={tag.templateTagId}
              tagId={tagId}
              tag={tag}
              clientId={client.id}
              client={client}
              locStrings={locStrings}
              waitingOnRequest={false}
              copy={copy}
            />
          )
        )}
      </div>
    </div>
  );
}

function BackButton({ onClick, disabled, copy }) {
  return (
    <Button
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={onClick}
      disabled={disabled}
      style={{ marginTop: 16, padding: 0 }}
    >
      {copy?.buttons?.back ?? 'Back'}
    </Button>
  );
}
